/**
 * Simulate Client — STAGING ONLY.
 *
 * Chat-like interface for testing Camila. Felipe writes as a prospect,
 * sees Camila's responses inline like WhatsApp. Maintains conversation
 * thread, shows typing indicator, supports starting new conversations.
 *
 * Access: navigate to /debug/simulate manually. No sidebar link.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Bot, MessageSquarePlus, Send } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiFetch } from '@/lib/query-client';

export const Route = createFileRoute('/_app/debug/simulate')({
  component: SimulateClientPage,
});

// ── Types ──────────────────────────────────────────────────────────

type Channel = 'whatsapp' | 'instagram' | 'messenger';

interface ChatMessage {
  readonly id: string;
  readonly role: 'contact' | 'ai' | 'system';
  readonly text: string;
  readonly timestamp: Date;
}

interface SimulateResponse {
  ok: boolean;
  channel: string;
  action: string;
  conversationId?: string;
  leadId?: string;
  phone?: string;
  contactId?: string;
  syntheticMessageId: string;
}

interface ApiMessage {
  id: string;
  direction: string;
  authorType?: string;
  bodyText?: string | null;
  sentAt: string;
}

// ── Helpers ────────────────────────────────────────────────────────

function fmtTime(date: Date): string {
  return date.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
}

function generatePhone(): string {
  return `+5690000${String(Date.now()).slice(-5)}`;
}

function generateContactId(channel: Channel): string {
  return `sim-${channel === 'instagram' ? 'ig' : 'msgr'}-${String(Date.now()).slice(-6)}`;
}

const CHANNEL_OPTIONS: { value: Channel; label: string; icon: string }[] = [
  { value: 'whatsapp', label: 'WhatsApp', icon: 'WA' },
  { value: 'instagram', label: 'Instagram', icon: 'IG' },
  { value: 'messenger', label: 'Messenger', icon: 'FB' },
];

// ── Main component ─────────────────────────────────────────────────

function SimulateClientPage() {
  // Setup state
  const [phase, setPhase] = useState<'setup' | 'chat'>('setup');
  const [channel, setChannel] = useState<Channel>('whatsapp');
  const [phone, setPhone] = useState(generatePhone);
  const [contactId, setContactId] = useState(() => generateContactId('instagram'));
  const [profileName, setProfileName] = useState('Felipe (simulado)');

  // Chat state
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [knownIds, setKnownIds] = useState<Set<string>>(new Set());

  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages, waiting]);

  // Focus input after send
  useEffect(() => {
    if (!sending && phase === 'chat') {
      inputRef.current?.focus();
    }
  }, [sending, phase]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // ── Poll for Camila's response ──────────────────────────────────

  const startPolling = useCallback(
    (convId: string, known: Set<string>) => {
      if (pollRef.current) clearInterval(pollRef.current);

      let attempts = 0;
      const maxAttempts = 30; // 60s at 2s intervals

      pollRef.current = setInterval(async () => {
        attempts++;
        if (attempts > maxAttempts) {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setWaiting(false);
          setMessages((prev) => [
            ...prev,
            {
              id: `sys-timeout-${Date.now()}`,
              role: 'system',
              text: 'Sin respuesta de Camila después de 60s.',
              timestamp: new Date(),
            },
          ]);
          return;
        }

        try {
          const data = await apiFetch<{ data: ApiMessage[] }>({
            path: `/v1/messaging/conversations/${convId}/messages?limit=50`,
          });

          const apiMsgs = data.data ?? [];
          const newAiMessages: ChatMessage[] = [];

          for (const m of apiMsgs) {
            if (known.has(m.id)) continue;
            if (m.authorType === 'ai' || (m.direction === 'outbound' && m.authorType !== 'system' && m.authorType !== 'contact')) {
              newAiMessages.push({
                id: m.id,
                role: 'ai',
                text: m.bodyText ?? '',
                timestamp: new Date(m.sentAt),
              });
              known.add(m.id);
            }
            // Also track inbound messages we haven't seen (e.g. from previous session)
            if (m.direction === 'inbound') {
              known.add(m.id);
            }
          }

          if (newAiMessages.length > 0) {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            setKnownIds(new Set(known));
            setMessages((prev) => [...prev, ...newAiMessages]);
            setWaiting(false);
          }
        } catch {
          // Silently retry — conversation may not be immediately queryable
        }
      }, 2000);
    },
    [],
  );

  // ── Send message ────────────────────────────────────────────────

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    setSending(true);
    setError(null);

    // Immediately show contact message
    const contactMsg: ChatMessage = {
      id: `local-${Date.now()}`,
      role: 'contact',
      text: trimmed,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, contactMsg]);
    setText('');

    try {
      const body: Record<string, unknown> = {
        channel,
        text: trimmed,
        profileName,
      };

      if (channel === 'whatsapp') {
        body.phone = phone;
      } else {
        body.contactId = contactId;
      }

      // Continue existing conversation if we have one
      if (conversationId) {
        body.conversationId = conversationId;
      }

      const res = await apiFetch<SimulateResponse>({
        path: '/v1/debug/simulate-inbound',
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        setError(`Simulación falló: ${res.action}`);
        setSending(false);
        return;
      }

      // Track the conversation ID
      const convId = res.conversationId!;
      if (!conversationId) {
        setConversationId(convId);
      }

      // Track this inbound message's syntheticMessageId
      const nextKnown = new Set(knownIds);
      nextKnown.add(res.syntheticMessageId);
      setKnownIds(nextKnown);

      // Start polling for Camila's response
      setWaiting(true);
      startPolling(convId, nextKnown);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  };

  // ── Start new conversation ──────────────────────────────────────

  const handleNewConversation = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    setConversationId(null);
    setMessages([]);
    setKnownIds(new Set());
    setWaiting(false);
    setError(null);
    setText('');
    if (channel === 'whatsapp') {
      setPhone(generatePhone());
    } else {
      setContactId(generateContactId(channel));
    }
  };

  // ── Start conversation from setup ───────────────────────────────

  const handleStart = () => {
    setPhase('chat');
    inputRef.current?.focus();
  };

  // ── Key handler ─────────────────────────────────────────────────

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Render: Setup phase ─────────────────────────────────────────

  if (phase === 'setup') {
    return (
      <div className="mx-auto max-w-lg space-y-6 p-6">
        <div>
          <h1 className="text-xl font-semibold text-[color:var(--color-fg)]">
            Chat con Camila
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-fg-subtle)]">
            Conversa como si fueras un prospecto. Los mensajes entran por
            la misma ruta que un webhook real. Solo funciona en staging.
          </p>
        </div>

        <div className="space-y-4">
          {/* Channel selector */}
          <div className="space-y-2">
            <Label>Canal</Label>
            <div className="flex gap-2">
              {CHANNEL_OPTIONS.map((ch) => (
                <button
                  key={ch.value}
                  type="button"
                  onClick={() => {
                    setChannel(ch.value);
                    if (ch.value !== 'whatsapp') {
                      setContactId(generateContactId(ch.value));
                    }
                  }}
                  className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
                    channel === ch.value
                      ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-[color:var(--color-primary-fg)]'
                      : 'border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] text-[color:var(--color-fg)] hover:bg-[color:var(--color-bg-overlay)]'
                  }`}
                >
                  <span className="mr-1.5 font-bold">{ch.icon}</span>
                  {ch.label}
                </button>
              ))}
            </div>
          </div>

          {/* Identity */}
          <div className="space-y-2">
            <Label htmlFor="profileName">Tu nombre (como contacto)</Label>
            <Input
              id="profileName"
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
              placeholder="Felipe (simulado)"
            />
          </div>

          {channel === 'whatsapp' ? (
            <div className="space-y-2">
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <p className="text-xs text-[color:var(--color-fg-subtle)]">
                Se genera uno nuevo. Cámbialo si quieres retomar una conversación anterior.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="contactId">
                Contact ID ({channel === 'instagram' ? 'IGSID' : 'PSID'})
              </Label>
              <Input
                id="contactId"
                value={contactId}
                onChange={(e) => setContactId(e.target.value)}
              />
            </div>
          )}

          <Button variant="primary" onClick={handleStart} className="w-full">
            Iniciar conversación
          </Button>
        </div>
      </div>
    );
  }

  // ── Render: Chat phase ──────────────────────────────────────────

  const channelLabel = CHANNEL_OPTIONS.find((c) => c.value === channel)!;
  const identifier = channel === 'whatsapp' ? phone : contactId;

  return (
    <div className="mx-auto flex h-[calc(100vh-4rem)] max-w-2xl flex-col">
      {/* ── Header ──────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 border-b px-4 py-3"
        style={{ borderColor: 'var(--color-border)' }}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span
              className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase"
              style={{
                background: 'var(--color-bg-overlay)',
                color: 'var(--color-fg-muted)',
              }}
            >
              {channelLabel.icon}
            </span>
            <span className="text-sm font-medium text-[color:var(--color-fg)]">
              {profileName}
            </span>
            <span className="text-xs text-[color:var(--color-fg-subtle)]">
              {identifier}
            </span>
          </div>
          {conversationId && (
            <Link
              to="/messaging"
              search={{ c: conversationId }}
              className="text-[11px] text-[color:var(--color-primary)] hover:underline"
            >
              Ver en bandeja
            </Link>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleNewConversation}
          className="gap-1.5 text-xs"
        >
          <MessageSquarePlus className="h-3.5 w-3.5" />
          Nueva
        </Button>
      </div>

      {/* ── Message thread ──────────────────────────────────────── */}
      <div
        ref={threadRef}
        className="flex-1 overflow-y-auto px-4 py-4"
        style={{ background: 'var(--color-bg)' }}
      >
        {messages.length === 0 && !waiting && (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-[color:var(--color-fg-subtle)]">
              Escribe un mensaje para empezar la conversación con Camila.
            </p>
          </div>
        )}

        <div className="space-y-1">
          {messages.map((msg) => (
            <ChatBubble key={msg.id} message={msg} contactName={profileName} />
          ))}

          {/* Typing indicator */}
          {waiting && <TypingIndicator />}
        </div>
      </div>

      {/* ── Composer ────────────────────────────────────────────── */}
      <div
        className="border-t px-4 py-3"
        style={{ borderColor: 'var(--color-border)' }}
      >
        {error && (
          <div
            className="mb-2 rounded-md px-3 py-2 text-xs"
            style={{ background: 'var(--color-danger-bg, #fef2f2)', color: 'var(--color-danger)' }}
          >
            {error}
          </div>
        )}
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Escribe como prospecto..."
            rows={1}
            className="flex-1 resize-none rounded-lg border px-3 py-2.5 text-sm focus-visible:outline-none"
            style={{
              background: 'var(--color-bg-elevated)',
              borderColor: 'var(--color-border)',
              color: 'var(--color-fg)',
            }}
            disabled={sending}
          />
          <Button
            variant="primary"
            size="sm"
            onClick={handleSend}
            disabled={sending || !text.trim()}
            className="self-end px-3"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="mt-1 text-[10px] text-[color:var(--color-fg-subtle)]">
          Ctrl+Enter para enviar
        </p>
      </div>
    </div>
  );
}

// ── Chat bubble ───────────────────────────────────────────────────

function ChatBubble({
  message,
  contactName,
}: {
  readonly message: ChatMessage;
  readonly contactName: string;
}) {
  if (message.role === 'system') {
    return (
      <div className="flex justify-center py-1">
        <span
          className="rounded-md px-3 py-1.5 text-xs italic"
          style={{
            background: 'var(--color-bubble-system, var(--color-bg-overlay))',
            color: 'var(--color-fg-muted)',
          }}
        >
          {message.text}
        </span>
      </div>
    );
  }

  const isContact = message.role === 'contact';

  return (
    <div className={`flex gap-2 py-1 ${isContact ? 'flex-row' : 'flex-row-reverse'}`}>
      {/* Avatar */}
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full">
        {isContact ? (
          <div
            className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold text-white"
            style={{ background: 'var(--color-fg-muted)' }}
          >
            {(contactName[0] ?? '?').toUpperCase()}
          </div>
        ) : (
          <div
            className="flex h-7 w-7 items-center justify-center rounded-full"
            style={{ background: 'var(--color-purple, #8b5cf6)', opacity: 0.15 }}
          >
            <Bot className="h-4 w-4" style={{ color: 'var(--color-purple, #8b5cf6)', opacity: 1 }} />
          </div>
        )}
      </div>

      <div className={`flex max-w-[75%] flex-col ${isContact ? 'items-start' : 'items-end'}`}>
        {/* Sender label */}
        <span
          className="mb-0.5 text-[11px] font-medium"
          style={{
            color: isContact ? 'var(--color-fg-muted)' : 'var(--color-purple, #8b5cf6)',
          }}
        >
          {isContact ? contactName : 'Camila (IA)'}
        </span>

        {/* Bubble */}
        <div
          className={`rounded-xl px-3 py-2 text-sm ${!isContact ? 'border border-solid' : ''}`}
          style={{
            background: isContact
              ? 'var(--color-bubble-inbound, var(--color-bg-elevated))'
              : 'var(--color-bubble-outbound, var(--color-primary))',
            color: isContact
              ? 'var(--color-fg)'
              : 'var(--color-bubble-outbound-text, var(--color-primary-fg))',
            borderColor: !isContact ? 'var(--color-purple, #8b5cf6)' : undefined,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {message.text}
        </div>

        {/* Timestamp */}
        <span className="mt-0.5 text-[10px] text-[color:var(--color-fg-subtle)]">
          {fmtTime(message.timestamp)}
        </span>
      </div>
    </div>
  );
}

// ── Typing indicator ──────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex gap-2 py-1">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full">
        <div
          className="flex h-7 w-7 items-center justify-center rounded-full"
          style={{ background: 'var(--color-purple, #8b5cf6)', opacity: 0.15 }}
        >
          <Bot className="h-4 w-4" style={{ color: 'var(--color-purple, #8b5cf6)', opacity: 1 }} />
        </div>
      </div>
      <div className="flex flex-col items-start">
        <span
          className="mb-0.5 text-[11px] font-medium"
          style={{ color: 'var(--color-purple, #8b5cf6)' }}
        >
          Camila (IA)
        </span>
        <div
          className="flex items-center gap-1 rounded-xl border border-solid px-4 py-3"
          style={{
            background: 'var(--color-bubble-outbound, var(--color-primary))',
            borderColor: 'var(--color-purple, #8b5cf6)',
          }}
        >
          <span className="typing-dot h-2 w-2 rounded-full" style={{ background: 'var(--color-purple, #8b5cf6)', animationDelay: '0ms' }} />
          <span className="typing-dot h-2 w-2 rounded-full" style={{ background: 'var(--color-purple, #8b5cf6)', animationDelay: '150ms' }} />
          <span className="typing-dot h-2 w-2 rounded-full" style={{ background: 'var(--color-purple, #8b5cf6)', animationDelay: '300ms' }} />
        </div>
        <span className="mt-0.5 text-[10px] text-[color:var(--color-fg-subtle)]">
          pensando...
        </span>
      </div>
      <style>{`
        @keyframes typing-bounce {
          0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
          30% { opacity: 1; transform: translateY(-4px); }
        }
        .typing-dot {
          animation: typing-bounce 1.2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
